import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  UserCircle,
  Search,
  FileText,
  Award,
  ListChecks,
  Upload,
  DollarSign,
  Star,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Use Radius as a Vendor",
  "description": "Step-by-step guide for vendors to find opportunities, submit bids, and get paid securely.",
  "step": [
    {
      "@type": "HowToStep",
      "name": "Create Your Vendor Profile",
      "text": "Sign up and build a comprehensive profile showcasing your capabilities and certifications."
    },
    {
      "@type": "HowToStep",
      "name": "Discover Relevant RFQs",
      "text": "Browse public RFQs that match your capabilities or receive private invitations."
    },
    {
      "@type": "HowToStep",
      "name": "Submit Your Bid",
      "text": "Create a professional bid with line-item pricing and delivery schedules."
    },
    {
      "@type": "HowToStep",
      "name": "Get Awarded the Job",
      "text": "Client selects the winning bid and payment is secured in escrow."
    },
    {
      "@type": "HowToStep",
      "name": "Manage Your Project",
      "text": "Track milestones, communicate with clients, and manage documentation."
    },
    {
      "@type": "HowToStep",
      "name": "Submit Proof of Delivery",
      "text": "For each milestone, submit delivery proof with photos and documents."
    },
    {
      "@type": "HowToStep",
      "name": "Receive Secure Payment",
      "text": "Payment is automatically released upon client approval."
    },
    {
      "@type": "HowToStep",
      "name": "Build Your Reputation",
      "text": "Receive reviews from clients to attract more opportunities."
    }
  ]
};

export default function HowItWorksVendors() {
  const steps = [
    {
      number: 1,
      icon: UserCircle,
      title: "Create Your Vendor Profile",
      description: "Sign up and build a comprehensive profile showcasing your capabilities, certifications, and past work.",
      details: [
        "Complete company information and contact details",
        "Upload certifications and credentials",
        "Select your capabilities and specializations",
        "Add a photo gallery of past projects",
        "Set up Stripe Connect for secure payments",
      ]
    },
    {
      number: 2,
      icon: Search,
      title: "Discover Relevant RFQs",
      description: "Browse public RFQs that match your capabilities or receive private invitations from clients.",
      details: [
        "Filter by category, location, and requirements",
        "See RFQs matching your certifications",
        "Get notified of new opportunities",
        "Receive private invitations from clients",
        "Review detailed specifications before bidding",
      ]
    },
    {
      number: 3,
      icon: FileText,
      title: "Submit Your Bid",
      description: "Create a professional, detailed bid with line-item pricing, quantity breaks, and delivery schedules.",
      details: [
        "Add line items with individual pricing",
        "Offer quantity-based discounts",
        "Set delivery schedules and timelines",
        "Upload supporting documents or samples",
        "Communicate with client through Q&A",
      ]
    },
    {
      number: 4,
      icon: Award,
      title: "Get Awarded the Job",
      description: "Client reviews all bids and selects the winner. Once awarded, the payment is securely held and you're ready to start.",
      details: [
        "Receive notification of award",
        "Review final purchase agreement",
        "Payment secured immediately",
        "Access project details and milestones",
        "Begin work with payment guarantee",
      ]
    },
    {
      number: 5,
      icon: ListChecks,
      title: "Manage Your Project",
      description: "Track milestones, communicate with the client, and manage all aspects of the project in one place.",
      details: [
        "View milestone schedule and deadlines",
        "Built-in messaging with client",
        "Upload project documents and updates",
        "Add tracking numbers for shipments",
        "Receive deadline reminders",
      ]
    },
    {
      number: 6,
      icon: Upload,
      title: "Submit Proof of Delivery",
      description: "For each completed milestone, submit delivery proof with photos, documents, and required information.",
      details: [
        "Upload photos of completed work or delivered goods",
        "Attach supporting documents (packing slips, receipts, etc.)",
        "Fill out delivery templates if required",
        "Provide shipment and tracking information",
        "Client reviews and approves submission",
      ]
    },
    {
      number: 7,
      icon: DollarSign,
      title: "Receive Secure Payment",
      description: "Once the client approves your delivery, payment is automatically released to your account.",
      details: [
        "Automatic payment release upon approval",
        "Funds transferred to your Stripe Connect account",
        "Receive payment notification",
        "Fast, secure bank deposits",
        "Transparent transaction history",
      ]
    },
    {
      number: 8,
      icon: Star,
      title: "Build Your Reputation",
      description: "Receive reviews from satisfied clients that help you win more opportunities on the platform.",
      details: [
        "Client leaves verified review and rating",
        "Reviews appear on your profile",
        "Build credibility with completed projects",
        "Higher ratings lead to more invitations",
        "Showcase your track record to future clients",
      ]
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="How It Works for Vendors"
        description="Step-by-step guide for vendors: create your profile, discover RFQs, submit bids, and get paid securely on Radius."
        canonical="/how-it-works/vendors"
        structuredData={howToSchema}
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              How It Works for <span className="text-primary">Vendors</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A straightforward path from creating your profile to getting paid securely for quality work delivered.
            </p>
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=vendor">
                Create Your Vendor Profile <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="container py-20">
        <div className="max-w-4xl mx-auto space-y-16">
          {steps.map((step, index) => (
            <div key={step.number} className="relative">
              {/* Connector Line */}
              {index !== steps.length - 1 && (
                <div className="absolute left-6 top-20 bottom-0 w-0.5 bg-border -translate-x-1/2 hidden md:block" />
              )}
              
              <div className="flex flex-col md:flex-row gap-6">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
                      {step.number}
                    </div>
                    <div className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-background border-2 border-primary flex items-center justify-center">
                      <step.icon className="h-3 w-3 text-primary" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                  
                  <ul className="space-y-2">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Summary */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Why Vendors Choose Us</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="font-semibold mb-2">✅ Payment Protection</h3>
              <p className="text-sm text-muted-foreground">
                Client payment is held securely before you start work. Complete your project and payment releases automatically, speeding up payment and reducing uncertainty.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="font-semibold mb-2">✅ Quality Opportunities</h3>
              <p className="text-sm text-muted-foreground">
                Every RFQ comes from verified clients with real projects and budgets. No time wasters—just serious buyers ready to work.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="font-semibold mb-2">✅ Clear Expectations</h3>
              <p className="text-sm text-muted-foreground">
                Detailed specifications, defined milestones, and agreed-upon deliverables from the start. No scope creep or surprises.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="font-semibold mb-2">✅ Build Your Brand</h3>
              <p className="text-sm text-muted-foreground">
                Every successful project earns you verified reviews that attract more clients and higher-value opportunities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-bold">Start Winning Quality Projects Today</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join our vendor marketplace and access bidding opportunities with payment protection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=vendor">
                Create Vendor Profile
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/for-vendors">
                Learn More About Benefits
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
